/*
 *   Old SIP (XSIP) summary — layout aligned with admin Old SIP detail reference.
 */

import { Fragment } from "react";
import { useNavigate } from "react-router-dom";

const pick = (d, ...keys) => {
    if (!d) return undefined;
    for (const k of keys) {
        let v = d;
        for (const part of String(k).split(".")) {
            v = v?.[part];
        }
        if (v !== undefined && v !== null && v !== "") return v;
    }
    return undefined;
};

const display = (v) => (v !== undefined && v !== null && v !== "" ? String(v) : "—");

const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

const formatInr = (amount) => {
    if (amount === undefined || amount === null || amount === "") return "—";
    const n = Number(amount);
    if (Number.isNaN(n)) return display(amount);
    return `₹${n.toLocaleString("en-IN")}`;
};

const FieldRow = ({ label, value, valueClassName = "color-outer-space" }) => (
    <div className="py-2">
        <p className="color-gray e-poppins-medium e-font-14 line-height-16px mb-1">{label}</p>
        <p className={`e-poppins-medium e-font-16 line-height-22px mb-0 ${valueClassName}`}>{value}</p>
    </div>
);

const SectionTitle = ({ children }) => (
    <div className="col-12 mt-4 mb-2">
        <p className="color-black e-poppins-semi-bold e-font-16 line-height-20px mb-0">{children}</p>
    </div>
);

const statusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "running" || s === "active") return "color-sea-green";
    return "color-outer-space";
};

/**
 * @param {{ data: object, oldSipId?: number|string }} props
 */
const OldSipSummaryView = ({ data, oldSipId }) => {
    const navigate = useNavigate();
    const d = data || {};
    const m = d.mandate && typeof d.mandate === "object" ? d.mandate : {};
    const b = d.bank && typeof d.bank === "object" ? d.bank : {};
    const o = d.origin && typeof d.origin === "object" ? d.origin : {};

    const schemeTitle = display(pick(d, "scheme_name", "name"));
    const installmentsPaid =
        pick(d, "installments_paid") ??
        (Array.isArray(d.installments) ? d.installments.filter((x) => x && x.is_complete === true).length : undefined);

    return (
        <Fragment>
            <div className="row">
                <div className="col-lg-10 col-12">
                    <div className="border-radius-24px border-all border-bright-gray p-4 bg-white">
                        <div className="border-bottom-1px border-bright-gray pb-3 d-flex justify-content-between align-items-start flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-12px flex-grow-1 min-width-0">
                                <div className="border-all border-bright-gray border-radius-8px p-2 flex-shrink-0">
                                    <img
                                        draggable={false}
                                        src={pick(d, "image") || ""}
                                        alt=""
                                        width={40}
                                        height={40}
                                        className="object-fit-contain"
                                        onError={(e) => {
                                            e.target.style.visibility = "hidden";
                                        }}
                                    />
                                </div>
                                <p className="color-black e-poppins-medium e-font-16 line-height-24px mb-0">{schemeTitle}</p>
                            </div>
                            <div className="text-end">
                                <p className="color-black e-poppins-semi-bold e-font-20 line-height-24px mb-0">
                                    <span className="e-inter-semi-bold">₹</span>
                                    {pick(d, "amount") != null && !Number.isNaN(Number(d.amount))
                                        ? Number(d.amount).toLocaleString("en-IN")
                                        : "—"}
                                </p>
                                <p className="color-gray e-poppins-regular e-font-12 line-height-18px mb-0">SIP amount</p>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-6">
                                <FieldRow label="ID" value={display(pick(d, "id") ?? oldSipId)} />
                                <FieldRow label="AMC name" value={display(pick(d, "amc_name"))} />
                                <FieldRow label="ISIN" value={display(pick(d, "isin"))} />
                                <FieldRow label="Fund ID" value={display(pick(d, "fund_id"))} />
                                <FieldRow label="Txn ID" value={display(pick(d, "txn_id"))} />
                                <FieldRow label="Txn type" value={display(pick(d, "txn_type"))} />
                                <FieldRow
                                    label="Status"
                                    value={display(pick(d, "status"))}
                                    valueClassName={statusClass(pick(d, "status"))}
                                />
                                <FieldRow label="SIP start date" value={display(pick(d, "sip_start_date", "start_date"))} />
                                <FieldRow label="Installments paid" value={display(installmentsPaid)} />
                                <FieldRow
                                    label="Total installments"
                                    value={display(pick(d, "total_installments", "total_number_of_installments"))}
                                />
                                <FieldRow label="User type" value={display(pick(d, "user_type", "origin.user_type"))} />
                                <FieldRow label="Top-up" value={yesNo(pick(d, "is_topup"))} />
                                <FieldRow label="Remarks" value={display(pick(d, "remarks"))} />
                            </div>
                            <div className="col-md-6 text-end">
                                <FieldRow label="SIP reg no" value={display(pick(d, "sip_reg_no"))} />
                                <FieldRow label="Scheme plan" value={display(pick(d, "scheme_plan"))} />
                                <FieldRow label="Scheme code" value={display(pick(d, "scheme_code"))} />
                                <FieldRow label="SIP order no" value={display(pick(d, "sip_order_no"))} />
                                <FieldRow label="Payment mode" value={display(pick(d, "payment_mode"))} />
                                <FieldRow label="Frequency" value={display(pick(d, "frequency"))} />
                                <FieldRow
                                    label="SIP registration date"
                                    value={display(pick(d, "sip_reg_date", "sip_created_on"))}
                                />
                                <FieldRow label="SIP end date" value={display(pick(d, "sip_end_date", "end_date"))} />
                                <FieldRow
                                    label="Installments pending"
                                    value={display(pick(d, "installments_pending", "remaining_installments"))}
                                />
                                <FieldRow label="Ordered by" value={display(pick(d, "ordered_by", "origin.ordered_by"))} />
                                <FieldRow label="Paused" value={yesNo(pick(d, "is_paused"))} />
                            </div>
                        </div>

                        <div className="row">
                            <SectionTitle>Mandate</SectionTitle>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <FieldRow label="Mandate ID" value={display(pick(m, "mandate_id") ?? pick(d, "mandate_id"))} />
                                <FieldRow label="Mandate status" value={display(pick(m, "mandate_status"))} />
                            </div>
                            <div className="col-md-6 text-end">
                                <FieldRow label="Mandate type" value={display(pick(m, "mandate_type") ?? pick(d, "mandate_type"))} />
                                <FieldRow label="Mandate amount" value={formatInr(pick(m, "mandate_amount"))} />
                            </div>
                        </div>

                        <div className="row">
                            <SectionTitle>Bank</SectionTitle>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <FieldRow label="Bank name" value={display(pick(b, "bank_name") ?? pick(d, "bank_name"))} />
                                <FieldRow label="IFSC" value={display(pick(b, "ifsc"))} />
                            </div>
                            <div className="col-md-6 text-end">
                                <FieldRow label="Account number" value={display(pick(b, "account_number") ?? pick(d, "account_number"))} />
                                <FieldRow label="Bank code" value={display(pick(b, "bank_code"))} />
                            </div>
                        </div>

                        <div className="row">
                            <SectionTitle>Origin</SectionTitle>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <FieldRow label="User ID" value={display(pick(o, "user_id"))} />
                                <FieldRow label="Ordered by" value={display(pick(o, "ordered_by") ?? pick(d, "ordered_by"))} />
                                <FieldRow label="Trax mode" value={display(pick(o, "trax_mode"))} />
                            </div>
                            <div className="col-md-6 text-end">
                                <FieldRow label="User type" value={display(pick(o, "user_type") ?? pick(d, "user_type"))} />
                                <FieldRow label="Client mode" value={display(pick(o, "client_mode"))} />
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0 mt-3 color-primary-color e-poppins-medium e-font-16"
                        onClick={() => navigate("/sip-manage", { state: { selectedTab: 1 } })}
                    >
                        Back to SIPs
                    </button>
                </div>
            </div>
        </Fragment>
    );
};

export default OldSipSummaryView;
